import React from 'react';
import { FolderKanban } from 'lucide-react';
import type { WorkspaceFolder } from '../../utils/workspaceFolders';
import { formatCost, formatRelativeTime, pluralize } from '../../utils/workspaceLibrary';
import { LibraryMenu, type MenuEntry } from './LibraryMenu';
import { colorTone } from './colors';
import { PeopleBadge, ProjectTile } from './ProjectTile';

export interface DropTargetHandlers {
    active: boolean;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
}

export interface ProjectCardProps {
    project: WorkspaceFolder;
    workspaceCount: number;
    folderCount: number;
    /** What its workspaces' generations cost together, in US dollars */
    totalCost?: number;
    childFolders: WorkspaceFolder[];
    /** Up to three newest thumbnails from anywhere in the project. */
    previews: string[];
    /** When a workspace in it was last saved */
    editedAt?: number;
    onOpen: () => void;
    menuItems: MenuEntry[];
    drop?: DropTargetHandlers;
}

/**
 * A project as a card: a mosaic of its newest pictures at the left (one tall, two small), its
 * tile and name, what is inside it, who it is shared with and when it was last touched.
 */
export const ProjectCard: React.FC<ProjectCardProps> = ({ project, workspaceCount, folderCount, totalCost, childFolders, previews, editedAt, onOpen, menuItems, drop }) => {
    const mosaic = previews.slice(0, 3);
    const people = project.sharedUserIds?.length ?? 0;

    return (
        <div
            role="listitem"
            className={`ns-lib-card ns-lib-card-project ${drop?.active ? 'ns-lib-drop' : ''}`}
            onDragOver={drop?.onDragOver}
            onDragLeave={drop?.onDragLeave}
            onDrop={drop?.onDrop}
            data-testid={`project-card-${project.id}`}
        >
            <button
                type="button"
                className="flex items-stretch gap-3 w-full min-w-0 text-left p-1.5 outline-none focus-visible:ring-4 focus-visible:ring-lavender-soft rounded-2xl"
                onClick={onOpen}
                aria-label={`Open project ${project.name}`}
            >
                <div className="ns-lib-mosaic" data-tone={colorTone(project.color)} aria-hidden="true">
                    {mosaic.length === 0 ? (
                        <span className="ns-lib-mosaic-empty"><FolderKanban /></span>
                    ) : mosaic.map((src, i) => (
                        <img key={i} src={src} alt="" loading="lazy" decoding="async" className={i === 0 ? 'ns-lib-mosaic-main' : ''} draggable={false} />
                    ))}
                </div>
                <div className="flex-1 min-w-0 py-1.5 pr-8 flex flex-col gap-1">
                    <div className="flex items-center gap-2 min-w-0">
                        <ProjectTile name={project.name} color={project.color} />
                        <h3 className="text-[15px] font-bold text-ink truncate" title={project.name}>{project.name}</h3>
                    </div>
                    <p className="ns-t-small">
                        {pluralize(workspaceCount, 'workspace')}
                        {folderCount > 0 && ` · ${pluralize(folderCount, 'folder')}`}
                        {totalCost && totalCost > 0 ? <span className="tabular-nums" data-card-cost> · {formatCost(totalCost)}</span> : null}
                    </p>
                    {childFolders.length > 0 && (
                        <p className="ns-t-meta truncate">{childFolders.slice(0, 3).map(f => f.name).join(' · ')}{childFolders.length > 3 ? ` · +${childFolders.length - 3}` : ''}</p>
                    )}
                    <div className="flex items-center gap-2 mt-auto pt-1">
                        <PeopleBadge count={people} />
                        {editedAt ? <span className="ns-t-meta">Edited {formatRelativeTime(editedAt)}</span> : null}
                    </div>
                </div>
            </button>

            <div className="absolute top-2 right-2">
                <LibraryMenu label={`More actions for ${project.name}`} items={menuItems} triggerClassName="ns-icon-btn ns-icon-btn-sm ns-lib-card-reveal ns-lib-card-reveal-soft" />
            </div>
        </div>
    );
};
